import React, { useEffect, useState, useRef } from "react";
import api, { apiErrorMessage } from "../lib/api";

export function AppointmentSlotPicker({ clinicId, doctorId, serviceId, initialDate = "", onSelect, excludedRange }) {
  const excludedStart = excludedRange?.start_datetime;
  const excludedEnd = excludedRange?.end_datetime;
  const [date, setDate] = useState(initialDate || new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedStart, setSelectedStart] = useState(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  useEffect(() => {
    setSlots([]); setSelectedStart(null); onSelectRef.current(null);
    if (!clinicId || !doctorId || !serviceId || !date) { setLoading(false); setError(""); return; }
    let active = true; setLoading(true); setError("");
    api.get("/appointments/slots", { params: { clinic_id: clinicId, doctor_id: doctorId, service_id: serviceId, date } })
      .then((r) => { if (active) setSlots((r.data.slots || []).filter(slot => !excludedStart || !excludedEnd || !(new Date(slot.start) < new Date(excludedEnd) && new Date(slot.end) > new Date(excludedStart)))); })
      .catch((e) => { if (active) { setSlots([]); setError(apiErrorMessage(e, "Unable to load available slots")); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [clinicId, doctorId, serviceId, date, excludedStart, excludedEnd]);
  return <div className="space-y-3">
    <label className="text-xs text-slate-600 block">Appointment date
      <input type="date" className="mg-input block w-full mt-1" value={date} onChange={(e) => setDate(e.target.value)} />
    </label>
    {loading && <p className="text-sm text-slate-500">Checking availability…</p>}
    {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
    {!loading && !error && slots.length === 0 && <p className="text-sm text-slate-500">No available slots for this date.</p>}
    <div className="grid grid-cols-3 gap-2" aria-label="Available appointment times">
      {!loading && !error && slots.map((slot) => <button type="button" key={slot.start} aria-pressed={selectedStart === slot.start} onClick={() => { setSelectedStart(slot.start); onSelectRef.current(slot); }} className={`border border-mg-medical/30 text-mg-medical rounded-lg px-2 py-2 text-sm hover:bg-mg-medical/10 ${selectedStart === slot.start ? "bg-mg-medical/10 ring-2 ring-mg-medical" : ""}`}>
        {formatBranchTime(slot.local_start)}
      </button>)}
    </div>
    {slots[0]?.timezone && <p className="text-xs text-slate-500">Times shown in branch timezone: {slots[0].timezone}</p>}
  </div>;
}

function formatBranchTime(localStart) {
  const value = String(localStart || "").slice(11, 16);
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return value;
  const suffix = hours >= 12 ? "PM" : "AM";
  return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

export function SlotRescheduleModal({ appointment, onClose, onSubmit, busy = false, excludedRange }) {
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  return <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Reschedule appointment">
    <div className="bg-white rounded-2xl p-6 max-w-md w-full">
      <h3 className="font-bold text-mg-navy text-lg mb-4">Choose a new available slot</h3>
      <AppointmentSlotPicker clinicId={appointment.clinic_id} doctorId={appointment.doctor_id} serviceId={appointment.service_id}
        initialDate={appointment.appointment_datetime?.slice(0, 10)} onSelect={setSelected} excludedRange={excludedRange} />
      {selected && <p className="mt-3 text-sm font-semibold text-emerald-700">Selected: {selected.local_start?.replace("T", " ").slice(0, 16)} ({selected.timezone})</p>}
      <textarea className="mg-input w-full mt-3" rows={2} placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
      <div className="flex gap-2 mt-5"><button type="button" onClick={onClose} className="mg-btn-secondary flex-1">Cancel</button><button type="button" disabled={!selected || busy} onClick={() => onSubmit(selected.start, reason)} className="mg-btn-primary flex-1">{busy ? "Saving…" : "Reschedule"}</button></div>
    </div>
  </div>;
}
